import {
  Button, Message, Modal, Section, Tag,
} from '@zolteam/zol-admin-ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { deleteTerminal } from '../../../services/airports/terminals/adminTerminalService';
import { useGetTerminal } from '../../../services/airports/terminals/adminTerminalQueries';
/**
 * Airport details component form for panel
 * @param {object} props
 * @param {string} props.airportId
 * @param {string} props.terminalId
 * @param {() => void} [props.handleClosePanel]
 * @returns {JSX.Element}
 */
export default function TerminalDetails({ airportId, terminalId, handleClosePanel }) {
  // local state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // hooks
  const { t } = useTranslation();
  const navigate = useNavigate();

  // mutations
  const {
    mutate: deleteTerminalMutate,
    isLoading: isLoadingDeleteTerminal,
    error: deleteTerminalError,
    isError: isErrorDeleteTerminal,
  } = useMutation(() => deleteTerminal(airportId, terminalId), {
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      handleClosePanel();
    },
  });

  // queries
  const { data: terminal, isLoading } = useGetTerminal(airportId, terminalId, {
    options: {
      enabled: !!airportId && !!terminalId,
    },
  });

  const handleConfirmDelete = () => {
    deleteTerminalMutate();
  };

  const handleGoToEdit = () => {
    navigate(`/airports/${airportId}/terminals/${terminalId}`);
  };

  // Extract error info safely for rendering
  // @ts-ignore
  const deleteTermErrorStatus = deleteTerminalError?.response?.status;
  // @ts-ignore
  const deleteTermErrorCode = deleteTerminalError?.response?.data?.code || deleteTerminalError?.code;

  return (
    <div className="d-flex f-column full-screen-height">
      <div className="d-flex f-row f-wrap justify-between margin-bottom-m">
        <div className="d-flex f-row gap-s">
          <p className="h1-default margin-right-m">{terminal?.name}</p>
          <Tag size="medium" label={terminal?.identityCode} />
        </div>
        <div className="d-flex f-row gap-s self-end">
          <Button
            onClick={handleGoToEdit}
            icon="edit"
            isLoading={isLoading}
          />
          <Button
            color="alert"
            icon="trash"
            onClick={() => setIsDeleteModalOpen(true)}
            isLoading={isLoadingDeleteTerminal}
          />
        </div>
      </div>
      <Section
        id="customer-details"
        title={t('terminalDetails.section.title')}
        isDisplay
      >
        <p className="p1-default">
          {t('terminalDetails.fields.terminalId')}
          {' : '}
          {terminal?.terminalId || '-'}
        </p>
        <p className="p1-default">
          {t('terminalDetails.fields.address')}
          {' : '}
          {terminal?.address || '-'}
        </p>
        <p className="p1-default">
          {t('terminalDetails.fields.phone')}
          {' : '}
          {terminal?.phone || '-'}
        </p>
        <p className="p1-default">
          {t('terminalDetails.fields.openingTime')}
          {' : '}
          {terminal?.openingTime || '-'}
        </p>
        <p className="p1-default">
          {t('terminalDetails.fields.closingTime')}
          {' : '}
          {terminal?.closingTime || '-'}
        </p>
      </Section>

      {isDeleteModalOpen ? (
        <Modal
          id="deleteTerminalModal"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title={t('terminalList.delete.title')}
          size="small"
        >
          {isErrorDeleteTerminal ? (
            <Message>
              {deleteTermErrorStatus === 409
                ? t(`apiErrors.${deleteTermErrorCode || 'AIRPORT_USED_IN_FLIGHT_BRIEF'}`)
                : t(`apiErrors.${deleteTermErrorCode || 'generic'}`)}
            </Message>
          ) : null}
          <div className="d-flex f-column gap-m">
            <p className="p1-default">
              {t('terminalList.delete.description')}
            </p>
            <div className="d-flex f-row justify-end gap-s">
              <Button
                color="neutral00"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="alert"
                onClick={handleConfirmDelete}
                isLoading={isLoadingDeleteTerminal}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
